import { useEffect, useMemo } from 'react';
import { useTracking } from 'react-tracking';
import { push, redirect } from 'redux-first-router';
import { useDispatch } from 'react-redux';
import { withAuth } from '@/utilities/queries';
import { formatDaysUntilEnd } from '../../../../utils/formatDaysUntilEnd';

import { Button, Grid, Text } from '@simplywallst/ui-core';

import DocumentHeader from '@/components/DocumentHeader';
import { Nbsp } from '@/components/nbsp';
import ReportSection from '@/components/ReportSection';
import { UserSectionTitle } from '@/components/User/styled';
import { GrandfatherCoupons } from '@/components/OurPlans/components/Plans/constants';

import useUser from '@/hooks/useUser';
import { useStripePortal } from '@/hooks/useStripePortal';
import { useTypeformDrawer } from '@/hooks/useTypeformDrawer';
import { useUserAccess } from '@/hooks/useUserAccess';
import { useUserPlans } from '@/hooks/useUserPlans';

import { differenceInMinutes } from 'date-fns';

import { cancelCancellation } from '@simplywallst/services';

import {
  ROUTE_DASHBOARD,
  ROUTE_PLANS,
  ROUTE_USER_PROFILE,
} from '@/constants/routes';

import { getPath } from '@/router/utils';

import { CenteredButton, TextContainer } from './styled';
import { useSnackbar } from '@/features/snackbar';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useFlag } from '@/features/unleash';
import { differenceInDays } from 'date-fns';

const now = Date.now();

export const SubscriptionAndBilling = () => {
  const { Track, trackEvent } = useTracking({
    type: 'track',
    subject: 'subscriptionAndBilling',
  });
  const dispatch = useDispatch();
  const { status: planStatus, name: planName, plans } = useUserPlans();
  const { data: userData } = useUser();
  const { data: userAccess } = useUserAccess();
  const { data: stripePortal } = useStripePortal();
  const flag = useFlag({ name: 'subscription-extension' });

  const { plan } = userData.billing;

  const planInterval = useMemo(() => {
    let planInterval = '';
    if (typeof plans !== 'undefined') {
      plans.forEach((p) => {
        Object.values(p).forEach((value) => {
          if (value.id === plan) {
            planInterval = value.interval;
          }
        });
      });
    }
    return planInterval;
  }, [plans, plan]);

  const planDataReady = planStatus !== 'pending' && planStatus !== 'error';

  useEffect(() => {
    if (userData.billing.status === 'trialing') {
      dispatch(redirect({ type: ROUTE_USER_PROFILE }));
    }
    // Non-stripe subscribers can't access this page
    if (userAccess.provider === 'stripe') {
      trackEvent({ action: 'landed' });
    } else {
      dispatch(redirect({ type: ROUTE_DASHBOARD }));
    }
  }, [dispatch, trackEvent, userAccess.provider, userData.billing.status]);

  const stripePortalUrl = stripePortal?.url;
  const stripeUrlSplit = stripePortalUrl?.split('/');
  const stripeUrlEnd =
    stripeUrlSplit && stripeUrlSplit[stripeUrlSplit.length - 1];

  const isLegacyCoupon = GrandfatherCoupons.includes(
    userData.billing.coupon.id
  );

  const {
    currentPeriodEnd,
    currentPeriodStart,
    consecutiveRenewalCount,
    subscriptionDiscountPercentOff,
  } = userData.billing;

  const makeTypeformPopup = useTypeformDrawer(
    flag.enabled ? 'fMCYaRxQ' : 'a1N1rZDc',
    {
      stripeurl: stripeUrlEnd,
      islegacyplan: isLegacyCoupon,
      planid: `${planName}-${planInterval}`,
      userid: userData.id,
      countryiso: userData.countryISO,
      current_period_start: new Date(currentPeriodStart).toDateString(),
      current_period_end: new Date(currentPeriodEnd).toDateString(),
      days_before_renewal: differenceInDays(currentPeriodEnd, now),
      consec_renewal_count: consecutiveRenewalCount,
      latest_invoice_coupon: subscriptionDiscountPercentOff,
    }
  );

  const { addSnackbar } = useSnackbar();
  const queryClient = useQueryClient();
  const { mutate, status } = useMutation({
    mutationFn: async () => {
      const response = await withAuth(cancelCancellation);
      return response;
    },
    onSuccess() {
      setTimeout(() => {
        queryClient.invalidateQueries({
          queryKey: ['USER_PROFILE'],
        });
        queryClient.invalidateQueries({
          queryKey: ['USER_ACCESS'],
        });
      }, 2000);
      addSnackbar({
        message: 'Thanks for resuming your subscription!',
        type: 'positive',
        lifeTime: 3000,
      });
    },
    onError() {
      addSnackbar({
        message: 'Error trying to resume subscription. Contact support',
        type: 'negative',
        lifeTime: 3000,
      });
    },
  });

  const plansUrl = getPath({ type: ROUTE_PLANS, payload: {} });

  const displayResumePlan = (): boolean => {
    return (
      userAccess.planType !== 'free' &&
      userData.billing.canceled &&
      differenceInMinutes(userData.billing.currentPeriodEnd, new Date()) > 240
    );
  };

  return (
    <Track>
      <DocumentHeader title="Subscription &amp; Billing" />
      <UserSectionTitle data-cy-id="user-subscription-and-billing-title">
        Subscription &amp; Billing
      </UserSectionTitle>
      <ReportSection>
        <Grid rowGap="8px" templateColumnsSm="35%">
          <TextContainer>
            <Text typography="xSmall" color="white">
              My Current Plan
            </Text>
            <Text typography="baseStrong" color="white">
              {planDataReady ? `${planName} Plan` : <Nbsp />}
            </Text>
          </TextContainer>
          {displayResumePlan() ? (
            <TextContainer>
              <Text typography="xSmall" color="white">
                <span>
                  {`Your plan cancels in ${formatDaysUntilEnd(
                    userData.billing.canceledAt
                  )}`}
                  .{' '}
                </span>
                <span>{`Resume your subscription to keep access to ${userAccess.planType} features.`}</span>
              </Text>
            </TextContainer>
          ) : null}
          {displayResumePlan() ? (
            <CenteredButton
              data-cy-id="resume-subscription-btn"
              styleType="louderLarge"
              onClick={() => mutate()}
              disabled={status === 'pending' || status === 'success'}
            >
              Resume My Subscription
            </CenteredButton>
          ) : null}
          <CenteredButton
            as="a"
            data-cy-id="subscription-change-plan-btn"
            styleType="louderLarge"
            onClick={(e) => {
              e.preventDefault();
              push(plansUrl);
            }}
            href={
              typeof window !== 'undefined'
                ? window.location.origin + '/plans'
                : undefined
            }
          >
            Change Plan
          </CenteredButton>
          <CenteredButton
            as="a"
            data-cy-id="subscription-download-invoices-btn"
            styleType="gentleLarge"
            href={stripePortalUrl}
          >
            Download Invoices
          </CenteredButton>
          <CenteredButton
            as="a"
            data-cy-id="subscription-update-payment-btn"
            styleType="gentleLarge"
            href={stripePortalUrl}
          >
            Update Payment Details
          </CenteredButton>
          {userAccess.planType === 'free' || displayResumePlan() ? null : (
            <Button
              data-cy-id="subscription-cancel-btn"
              styleType="gentleLarge"
              onClick={makeTypeformPopup}
            >
              Cancel My Subscription
            </Button>
          )}
        </Grid>
      </ReportSection>
    </Track>
  );
};
